import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { format, isValid } from "date-fns"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formats a date string, Date object, or timestamp with a custom pattern.
 * Safely returns a fallback string if the date is null, undefined, or invalid.
 * Supports standard ISO dates as well as DD-MM-YYYY strings.
 *
 * @param date The date to format
 * @param pattern The date-fns format string (default: "dd-MMM-yyyy")
 * @param fallback Fallback string if invalid or null/undefined (default: "-")
 */
export function formatDate(
  date?: Date | string | number | null,
  pattern: string = "dd-MMM-yyyy",
  fallback: string = "-"
): string {
  if (!date) return fallback;
  try {
    let d: Date;
    if (typeof date === "string") {
      const trimmed = date.trim();
      const ddmmyyyyMatch = trimmed.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
      if (ddmmyyyyMatch) {
        const [, day, month, year] = ddmmyyyyMatch;
        d = new Date(Number(year), Number(month) - 1, Number(day));
      } else {
        d = new Date(trimmed);
      }
    } else if (typeof date === "number") {
      d = new Date(date);
    } else {
      d = date;
    }

    if (isValid(d)) {
      return format(d, pattern);
    }
  } catch {
    // ignore parse error
  }
  return String(date);
}

/**
 * Formats a date string or Date object with date and time (default: "dd-MMM-yyyy HH:mm").
 */
export function formatDateTime(
  date?: Date | string | number | null,
  pattern: string = "dd-MMM-yyyy HH:mm",
  fallback: string = "-"
): string {
  return formatDate(date, pattern, fallback);
}


/**
 * Generates initials from a name
 * @param name The full name to generate initials from
 * @param maxLength Maximum number of initials to generate (default: 2)
 * @returns The generated initials in uppercase
 */
export function generateInitials(name: string, maxLength: number = 2): string {
  return name
    .split(' ')
    .filter(segment => segment.length > 0)
    .map(segment => segment[0])
    .join('')
    .slice(0, maxLength)
    .toUpperCase();
}

/**
 * Generates a random background color for avatars
 * @param seed Optional seed to generate consistent colors for the same input
 * @returns A CSS color string
 */
export function generateAvatarBackgroundColor(seed?: string | number): string {
  const colors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
    '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2',
    '#F8B739', '#52B788', '#E76F51', '#8E44AD', '#3498DB',
    '#2ECC71', '#E74C3C', '#F39C12', '#9B59B6', '#1ABC9C'
  ];

  if (seed !== undefined) {
    const numericSeed = typeof seed === 'string'
      ? seed.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
      : seed;
    return colors[numericSeed % colors.length];
  }

  return colors[Math.floor(Math.random() * colors.length)];
}

/**
 * Formats a date as a relative time string (e.g., "2 minutes ago", "3 hours ago")
 * @param date The date to format (can be a Date object or a string)
 * @returns A human-readable relative time string
 */
export function formatTimeAgo(date: Date | string | null | undefined): string {
  if (!date) return '—';

  const now = new Date();
  const past = new Date(date);
  const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

  if (diffInSeconds < 0) return 'just now';

  const intervals = [
    { label: 'year', seconds: 31536000 },
    { label: 'month', seconds: 2592000 },
    { label: 'day', seconds: 86400 },
    { label: 'hour', seconds: 3600 },
    { label: 'minute', seconds: 60 },
    { label: 'second', seconds: 1 }
  ];

  for (const interval of intervals) {
    const count = Math.floor(diffInSeconds / interval.seconds);
    if (count >= 1) {
      return `${count} ${interval.label}${count !== 1 ? 's' : ''} ago`;
    }
  }

  return 'just now';
}

/**
 * Capitalizes the first letter of a string
 * @param str The string to capitalize
 * @returns The string with the first letter uppercased
 */
export function capitalizeFirst(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}
